import React, { useState } from 'react';
import { X, Calculator, TrendingUp, DollarSign, Scale } from 'lucide-react';
import { useTheme } from '../arrangement/ThemeContext';
export function THCCalculatorModal({ isOpen, onClose, leaderboardData = [] }) {
  const [formData, setFormData] = useState({
    productName: '',
    productType: 'flower',
    thcPercent: '',
    weight: '',
    weightUnit: 'g',
    price: '',
    thcPerServing: '',
    servings: ''
  });
  
  const [results, setResults] = useState(null);

  const { colors } = useTheme();

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const calculateTHC = () => {
    const { productType, thcPercent, weight, weightUnit, price, thcPerServing, servings } = formData;
    
    let totalThcMg = 0;
    
    // For edibles with direct THC per serving info
    if (productType === 'edible' && thcPerServing && servings) {
      totalThcMg = parseFloat(thcPerServing) * parseFloat(servings);
    } 
    // For products with weight and THC percentage
    else if (thcPercent && weight) {
      let weightInMg = 0;
      const weightNum = parseFloat(weight);
      
      // Convert to mg based on unit
      switch(weightUnit) {
        case 'g':
          weightInMg = weightNum * 1000;
          break;
        case 'mg':
          weightInMg = weightNum;
          break;
        case 'oz':
          weightInMg = weightNum * 28349.5; // 1 oz = 28.3495g
          break;
        case 'lb':
          weightInMg = weightNum * 453592; // 1 lb = 453.592g
          break;
        case 'ml':
          weightInMg = weightNum * 1000; // Assume 1ml = 1g for oils
          break;
        default:
          weightInMg = weightNum * 1000;
      }
      
      totalThcMg = (parseFloat(thcPercent) / 100) * weightInMg;
    }
    
    const priceNum = parseFloat(price);
    
    if (totalThcMg > 0 && priceNum > 0) {
      const thcMgPerDollar = totalThcMg / priceNum;
      const pricePerMgThc = priceNum / totalThcMg;
      
      // Calculate rank based on leaderboard data
      let estimatedRank = 1;
      if (leaderboardData.length > 0) {
        estimatedRank = leaderboardData.filter(item => 
          item.thc_mg_per_dollar > thcMgPerDollar
        ).length + 1;
      }
      
      // Determine value tier
      let valueTier = 'GOOD';
      let tierColor = '#666';
      let tierGradient = 'linear-gradient(135deg, #666 0%, #555 100%)';
      
      if (estimatedRank <= 10) {
        valueTier = 'ELITE';
        tierColor = '#00d4aa';
        tierGradient = 'linear-gradient(135deg, #00d4aa 0%, #10b981 100%)';
      } else if (estimatedRank <= 25) {
        valueTier = 'GREAT';
        tierColor = '#f59e0b';
        tierGradient = 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)';
      }
      
      setResults({
        totalThcMg,
        thcMgPerDollar,
        pricePerMgThc,
        estimatedRank,
        valueTier,
        tierColor,
        tierGradient
      });
    }
  };

  const resetForm = () => {
    setFormData({
      productName: '',
      productType: 'flower',
      thcPercent: '',
      weight: '',
      weightUnit: 'g',
      price: '',
      thcPerServing: '',
      servings: ''
    });
    setResults(null);
  };

  if (!isOpen) return null;

  const isEdibleMode = formData.productType === 'edible';

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.85)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: '#1a1a1a',
        borderRadius: '16px',
        border: '1px solid #333',
        width: '100%',
        maxWidth: '600px',
        maxHeight: '90vh',
        overflow: 'auto',
        position: 'relative'
      }}>
        {/* Header */}
        <div style={{
          padding: '24px',
          borderBottom: `1px solid ${colors.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'sticky',
          top: 0,
          backgroundColor: '#1a1a1a',
          zIndex: 10
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Calculator size={24} style={{ color: '#f59e0b' }} />
            <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: '#e5e5e5' }}>
              Calculate Your Product's Value
            </h2>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: '#888',
              cursor: 'pointer',
              padding: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '8px',
              transition: 'all 0.2s'
            }}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#333'}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <div style={{ padding: '24px' }}>
          {/* Product Name */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontSize: '14px', 
              fontWeight: '600', 
              color: '#e5e5e5' 
            }}>
              Product Name (Optional)
            </label>
            <input
              type="text"
              value={formData.productName}
              onChange={(e) => handleInputChange('productName', e.target.value)}
              placeholder="e.g., Blue Dream Flower"
              style={{
                width: '95%',
                padding: '12px',
                backgroundColor: '#2a2a2a',
                border: '1px solid #333',
                borderRadius: '8px',
                color: '#e5e5e5',
                fontSize: '14px'
              }}
            />
          </div>

          {/* Product Type */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontSize: '14px', 
              fontWeight: '600', 
              color: '#e5e5e5' 
            }}>
              Product Type
            </label>
            <select
              value={formData.productType}
              onChange={(e) => handleInputChange('productType', e.target.value)}
              style={{
                width: '104%',
                padding: '12px',
                backgroundColor: '#2a2a2a',
                border: '1px solid #333',
                borderRadius: '8px',
                color: '#e5e5e5',
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              <option value="flower">Flower</option>
              <option value="pre-roll">Pre-roll</option>
              <option value="edible">Edible/Gummy</option>
              <option value="cartridge">Cartridge/Vape</option>
              <option value="concentrate">Concentrate</option>
            </select>
          </div>

          {/* Conditional Fields */}
          {isEdibleMode ? (
            <>
              {/* THC per Serving */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontSize: '14px', 
                  fontWeight: '600', 
                  color: '#e5e5e5' 
                }}>
                  THC per Serving (mg)
                </label>
                <input
                  type="number"
                  value={formData.thcPerServing}
                  onChange={(e) => handleInputChange('thcPerServing', e.target.value)}
                  placeholder="e.g., 10"
                  style={{
                    width: '95%',
                    padding: '12px',
                    backgroundColor: '#2a2a2a',
                    border: '1px solid #333',
                    borderRadius: '8px',
                    color: '#e5e5e5',
                    fontSize: '14px'
                  }}
                />
              </div>

              {/* Number of Servings */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontSize: '14px', 
                  fontWeight: '600', 
                  color: '#e5e5e5' 
                }}>
                  Number of Servings/Pieces
                </label>
                <input
                  type="number"
                  value={formData.servings}
                  onChange={(e) => handleInputChange('servings', e.target.value)}
                  placeholder="e.g., 10"
                  style={{
                    width: '95%',
                    padding: '12px',
                    backgroundColor: '#2a2a2a',
                    border: '1px solid #333',
                    borderRadius: '8px',
                    color: '#e5e5e5',
                    fontSize: '14px'
                  }}
                />
              </div>
            </>
          ) : (
            <>
              {/* THC Percentage */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontSize: '14px', 
                  fontWeight: '600', 
                  color: '#e5e5e5' 
                }}>
                  THC Percentage (%)
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={formData.thcPercent}
                  onChange={(e) => handleInputChange('thcPercent', e.target.value)}
                  placeholder="e.g., 24.5"
                  style={{
                    width: '95%',
                    padding: '12px',
                    backgroundColor: '#2a2a2a',
                    border: '1px solid #333',
                    borderRadius: '8px',
                    color: '#e5e5e5',
                    fontSize: '14px'
                  }}
                />
              </div>

              {/* Weight */}
              <div style={{ marginBottom: '20px', width: '104%' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontSize: '14px', 
                  fontWeight: '600', 
                  color: '#e5e5e5' 
                }}>
                  Weight/Volume
                </label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.weight}
                    onChange={(e) => handleInputChange('weight', e.target.value)}
                    placeholder="e.g., 3.5"
                    style={{
                      flex: 1,
                      padding: '12px',
                      backgroundColor: '#2a2a2a',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: '#e5e5e5',
                      fontSize: '14px'
                    }}
                  />
                  <select
                    value={formData.weightUnit}
                    onChange={(e) => handleInputChange('weightUnit', e.target.value)}
                    style={{
                      padding: '12px',
                      backgroundColor: '#2a2a2a',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: '#e5e5e5',
                      fontSize: '14px',
                      cursor: 'pointer',
                      minWidth: '80px'
                    }}
                  >
                    <option value="g">grams</option>
                    <option value="mg">mg</option>
                    <option value="oz">oz</option>
                    <option value="lb">lb</option>
                    <option value="ml">ml</option>
                  </select>
                </div>
              </div>
            </>
          )}

          {/* Price */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontSize: '14px', 
              fontWeight: '600', 
              color: '#e5e5e5' 
            }}>
              Price ($)
            </label>
            <input
              type="number"
              step="0.01"
              value={formData.price}
              onChange={(e) => handleInputChange('price', e.target.value)}
              placeholder="e.g., 35.00"
              style={{
                width: '95%',
                padding: '12px',
                backgroundColor: '#2a2a2a',
                border: '1px solid #333',
                borderRadius: '8px',
                color: '#e5e5e5',
                fontSize: '14px'
              }}
            />
          </div>

          {/* Calculate Button */}
          <button
            onClick={calculateTHC}
            style={{
              width: '100%',
              padding: '14px',
              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              color: '#0a0a0a',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '700',
              cursor: 'pointer',
              marginBottom: '16px'
            }}
          >
            Calculate Value
          </button>

          {/* Results */}
          {results && (
            <div style={{
              background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(245, 158, 11, 0.05) 100%)',
              border: '1px solid rgba(245, 158, 11, 0.3)',
              borderRadius: '12px',
              padding: '24px',
              marginTop: '24px'
            }}>
              <h3 style={{ 
                margin: '0 0 20px 0', 
                fontSize: '18px', 
                fontWeight: '700', 
                color: '#f59e0b',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <TrendingUp size={20} />
                Your Results
              </h3>

              {formData.productName && (
                <div style={{ 
                  marginBottom: '16px', 
                  paddingBottom: '16px', 
                  borderBottom: '1px solid rgba(245, 158, 11, 0.2)' 
                }}>
                  <p style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#e5e5e5' }}>
                    {formData.productName}
                  </p>
                </div>
              )}

              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: '1fr 1fr', 
                gap: '16px',
                marginBottom: '20px'
              }}>
                <div style={{
                  background: colors.bgCard,
                  padding: '16px',
                  borderRadius: '8px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '24px', fontWeight: '800', color: '#e5e5e5', marginBottom: '4px' }}>
                    {results.totalThcMg.toFixed(1)}mg
                  </div>
                  <div style={{ fontSize: '12px', color: '#888', textTransform: 'uppercase' }}>
                    Total THC
                  </div>
                </div>

                <div style={{
                  background: colors.bgCard,
                  padding: '16px',
                  borderRadius: '8px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '24px', fontWeight: '800', color: '#00d4aa', marginBottom: '4px' }}>
                    ${formData.price}
                  </div>
                  <div style={{ fontSize: '12px', color: '#888', textTransform: 'uppercase' }}>
                    Price
                  </div>
                </div>

                <div style={{
                  background: 'rgba(245, 158, 11, 0.2)',
                  padding: '16px',
                  borderRadius: '8px',
                  textAlign: 'center',
                  border: '1px solid rgba(245, 158, 11, 0.3)'
                }}>
                  <div style={{ fontSize: '24px', fontWeight: '800', color: '#f59e0b', marginBottom: '4px' }}>
                    {results.thcMgPerDollar.toFixed(1)}
                  </div>
                  <div style={{ fontSize: '12px', color: '#888', textTransform: 'uppercase' }}>
                    THC mg/$
                  </div>
                </div>

                <div style={{
                  background: colors.bgCard,
                  padding: '16px',
                  borderRadius: '8px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '24px', fontWeight: '800', color: '#e5e5e5', marginBottom: '4px' }}>
                    ${results.pricePerMgThc.toFixed(3)}
                  </div>
                  <div style={{ fontSize: '12px', color: '#888', textTransform: 'uppercase' }}>
                    Per mg THC
                  </div>
                </div>
              </div>

              {/* Rank & Tier */}
              <div style={{
                background: results.tierGradient,
                padding: '16px',
                borderRadius: '8px',
                textAlign: 'center',
                marginBottom: '12px'
              }}>
                <div style={{ 
                  fontSize: '14px', 
                  color: results.estimatedRank <= 25 ? '#0a0a0a' : '#e5e5e5',
                  marginBottom: '8px',
                  fontWeight: '600'
                }}>
                  Estimated Leaderboard Rank
                </div>
                <div style={{ 
                  fontSize: '32px', 
                  fontWeight: '800', 
                  color: results.estimatedRank <= 25 ? '#0a0a0a' : 'white',
                  marginBottom: '8px'
                }}>
                  #{results.estimatedRank}
                </div>
                <div style={{ 
                  fontSize: '16px', 
                  fontWeight: '700', 
                  color: results.estimatedRank <= 25 ? '#0a0a0a' : 'white',
                  textTransform: 'uppercase',
                  letterSpacing: '1px'
                }}>
                  {results.valueTier} VALUE
                </div>
              </div>

              <button
                onClick={resetForm}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: '#333',
                  color: '#e5e5e5',
                  border: '1px solid #444',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Calculate Another Product
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}